import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, FileDown, ShieldCheck, Eye, Loader2 } from "lucide-react";
import { HERO_STATS, LD_PAIN_POINTS } from "./data";
import SamplePackModal from "../../components/SamplePackModal";
import RequestAccessModal from "../../components/RequestAccessModal";
import { useAuth } from "../../lib/auth";

export function Hero() {
  const [showSample, setShowSample] = useState(false);
  const [showReq, setShowReq] = useState(false);
  const [demoLoading, setDemoLoading] = useState(false);
  const { demoLogin } = useAuth();
  const navigate = useNavigate();

  const startDemo = async () => {
    if (demoLoading) return;
    setDemoLoading(true);
    try {
      const u = await demoLogin();
      navigate(u?.is_demo ? "/assistant?demo=1" : "/dashboard");
    } catch (e) {
      console.error("demo-login failed", e);
      setDemoLoading(false);
    }
  };

  return (
    <section className="relative overflow-hidden">
      <SamplePackModal open={showSample} onClose={() => setShowSample(false)} />
      <RequestAccessModal open={showReq} onClose={() => setShowReq(false)} />
      <div className="absolute inset-0 tactical-grid opacity-[0.10]" />
      <div className="absolute inset-x-0 top-0 h-[480px] bg-gradient-to-b from-[var(--cyan)]/[0.04] to-transparent pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6 pt-24 pb-16">
        <div className="flex items-center gap-3 mb-6 slide-up">
          <span className="overline">// FOR_AUSTRALIAN_DEFENCE_CONTRACTORS</span>
        </div>
        <h1
          className="font-display font-bold text-5xl md:text-7xl lg:text-8xl leading-[0.95] uppercase tracking-tight max-w-5xl slide-up delay-1"
          data-testid="hero-title"
        >
          Prove your cyber team is <span className="text-[var(--cyan)] cyan-text-glow">mission-ready</span> —
          on the day procurement asks.
        </h1>
        <p className="mt-8 max-w-2xl text-lg text-[var(--text-2)] slide-up delay-2">
          <span className="text-white font-semibold">DefDossier — Tender-Ready Evidence for Defence Cyber Teams.</span>
          {" "}Train, certify, and continuously recertify your defensive cyber
          workforce — and render a signed ISM &middot; Essential&nbsp;Eight &middot; MITRE compliance pack
          PDF you can attach to any ADF or DoD tender response.
        </p>
        <div className="mt-10 flex flex-wrap gap-4 slide-up delay-3">
          <button onClick={startDemo} disabled={demoLoading} className="btn-primary inline-flex items-center gap-2 disabled:opacity-50" data-testid="hero-demo">
            {demoLoading ? <Loader2 size={18} className="animate-spin" /> : <Eye size={18} />}
            {demoLoading ? "Loading demo…" : "Try the Live Demo"} <ArrowRight size={18} />
          </button>
          <button onClick={() => setShowReq(true)} className="btn-ghost inline-flex items-center gap-2" data-testid="hero-request-access">
            <ShieldCheck size={16} /> Request Full Access
          </button>
          <a href="#evidence" onClick={(e) => { e.preventDefault(); setShowSample(true); }}
             className="btn-ghost inline-flex items-center gap-2" data-testid="hero-brief">
            <FileDown size={16} /> See What Procurement Sees
          </a>
        </div>

        {/* Product screenshot — credibility lever, shown as a real artefact rather than a background. */}
        <div className="mt-16 slide-up delay-3 relative" data-testid="hero-product-shot">
          <div className="absolute -inset-2 bg-[var(--cyan)]/10 blur-2xl opacity-50 pointer-events-none" />
          <div className="relative border border-[var(--border)] overflow-hidden bg-[var(--surface)]">
            <div className="flex items-center gap-2 px-4 py-2 border-b border-[var(--border)] bg-[var(--bg)]">
              <div className="flex gap-1.5">
                <div className="w-2.5 h-2.5 rounded-full bg-[var(--text-2)]/40" />
                <div className="w-2.5 h-2.5 rounded-full bg-[var(--text-2)]/40" />
                <div className="w-2.5 h-2.5 rounded-full bg-[var(--cyan)]/60" />
              </div>
              <div className="font-mono text-[10px] tracking-widest uppercase text-[var(--text-2)] ml-3">
                defdossier.com / dashboard
              </div>
            </div>
            <img
              src="/img/hero-product.jpg"
              alt="DefDossier compliance dashboard showing the daily Sentinel briefing, CISA KEV intel feed, training KPIs and queued exercises."
              className="block w-full"
              loading="eager"
            />
          </div>
        </div>

        <div className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-px border border-[var(--border)] bg-[var(--border)]">
          {HERO_STATS.map((s) => (
            <div key={s.k} className="bg-[var(--bg)] p-6">
              <div className="overline">{s.k}</div>
              <div className="font-mono text-2xl text-white mt-1">{s.v}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function MissionSection() {
  return (
    <section id="mission" className="border-t border-[var(--border)]">
      <div className="max-w-7xl mx-auto px-6 py-24 grid md:grid-cols-12 gap-10">
        <div className="md:col-span-4">
          <div className="overline">// 01_BUILT_FOR</div>
          <h2 className="font-display font-bold text-4xl uppercase mt-3">L&amp;D leaders<br/>at defence contractors.</h2>
          <p className="mt-6 text-sm text-[var(--text-2)] leading-relaxed">
            If you have ever spent six weeks rebuilding a training-evidence binder for an ASD or DoD tender response,
            this platform is for you. Defensive cyber roles are continuously contested — your evidence should be too.
          </p>
        </div>
        <div className="md:col-span-8 grid sm:grid-cols-2 gap-5">
          {LD_PAIN_POINTS.map((p) => {
            const Icon = p.icon;
            return (
              <div
                key={p.title}
                className="flat-card p-5"
                data-testid={`ld-card-${p.title.replace(/\s+/g, "-").toLowerCase()}`}
              >
                <div className="flex items-center gap-3 mb-3">
                  <Icon size={20} color="var(--cyan)" strokeWidth={1.5} />
                  <div className="overline">// {p.title.split(" ")[0].toUpperCase()}</div>
                </div>
                <h3 className="font-display font-bold text-lg uppercase">{p.title}</h3>
                <p className="mt-2 text-sm text-[var(--text-2)] leading-relaxed">{p.body}</p>
              </div>
            );
          })}
        </div>
      </div>

      <div className="border-t border-[var(--border)] bg-[var(--surface)]">
        <div className="max-w-7xl mx-auto px-6 py-6 flex items-center gap-3 text-sm font-mono text-[var(--text-2)]">
          <ShieldCheck size={16} color="var(--green)" />
          <span>
            <span className="text-white">Every credential is a cryptographically-signed Open Badge.</span>
            {" "}Hiring managers can verify without a portal login at <span className="text-[var(--cyan)]">/verify/&lt;badge-id&gt;</span>.
          </span>
        </div>
      </div>
    </section>
  );
}
